/**
 * Tool UI for execute_command — a one-line row (command + status) that
 * expands, on click only, to:
 *  - Live streaming output while running
 *  - Terminal-like stdout/stderr display
 *
 * Output over the tool envelope's inline limit (4KB) is offloaded to a
 * workspace file, leaving a pointer with no `stdout` or `exitCode`. Live
 * streaming hides that while the command runs, but on reload the row would
 * otherwise show a finished command as having produced nothing — so the
 * payload is fetched back through `useOffloadedResult`.
 */
import { useState, useEffect, useRef } from 'react';
import { makeAssistantToolUI } from '@assistant-ui/react';
import { useShellOutputStream } from '../useShellOutputStream';
import { useOffloadedResult, fmtBytes, type EnvelopeFields } from './useOffloadedResult';
import { Terminal, XCircle, Download } from 'lucide-react';
import { ToolRow, RowAction, DetailPanel, DetailPre, plainText } from '../toolRow';

function formatMs(ms: number): string {
  return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(ms < 10_000 ? 1 : 0)}s`;
}

interface LiveChunk {
  stream: 'stdout' | 'stderr';
  data: string;
  t: number;
}

interface CommandArgs {
  command: string;
  timeoutMs?: number;
  _thought?: string;
}

interface CommandResult extends EnvelopeFields {
  exitCode?: number;
  stdout?: string;
  stderr?: string;
  durationMs?: number;
  error?: string;
  liveOutput?: LiveChunk[];
}

export const ScriptToolUI = makeAssistantToolUI<CommandArgs, CommandResult>({
  toolName: 'execute_command',
  render: function CommandToolRender(props: any) {
    const { args, status } = props;
    const rawResult: CommandResult | undefined = props.result;
    const [expanded, setExpanded] = useState(false);
    const outputRef = useRef<HTMLPreElement>(null);
    const isRunning = status.type === 'running';

    // Large output lives in a workspace file; fold it back in once loaded so
    // the exit code, timings and stdout/stderr all render normally.
    const offload = useOffloadedResult<CommandResult>(rawResult);
    const result: CommandResult | undefined = offload.data ?? rawResult;
    const offloadPending = offload.isOffloaded && !offload.data;

    const succeeded = result && !offloadPending && !result.error
      && (result.exitCode === 0 || result.exitCode === undefined);
    const failed = result && !offloadPending
      && (result.error || (result.exitCode !== undefined && result.exitCode !== 0));

    const sseChunks = useShellOutputStream();

    // No auto-expand on live output: the row stays one line until clicked.

    useEffect(() => {
      if (outputRef.current) {
        outputRef.current.scrollTop = outputRef.current.scrollHeight;
      }
    }, [expanded, sseChunks.length, result?.liveOutput?.length, result?.stdout]);

    const liveText = isRunning
      ? (sseChunks.length > 0
          ? sseChunks.map((c) => c.data).join('')
          : (result?.liveOutput?.map((c: LiveChunk) => c.data).join('') ?? ''))
      : (result?.liveOutput?.map((c: LiveChunk) => c.data).join('') ?? '');
    const showLive = isRunning && liveText.length > 0;

    const firstLine = args.command ? args.command.split('\n')[0] : '';
    // The model's _thought says why; the command itself is in the tooltip and the details.
    const thought = plainText(args._thought);
    const durationMeta = result?.durationMs != null ? formatMs(result.durationMs) : undefined;

    return (
      <ToolRow
        className="ai-script-row"
        icon={failed ? <XCircle className="text-red-500" /> : <Terminal />}
        label={thought || <span className="ai-script-row__command">{firstLine ? `$ ${firstLine}` : 'shell command'}</span>}
        title={args.command}
        mono={!thought}
        running={(isRunning && !result) || offload.loading}
        tone={failed ? 'error' : 'default'}
        meta={
          failed ? <span className="text-red-600">exit {result!.exitCode ?? 'error'}</span>
            : succeeded ? durationMeta
            : offloadPending && !isRunning ? (offload.sizeBytes > 0 ? fmtBytes(offload.sizeBytes) : 'output stored')
            : isRunning && args.timeoutMs && args.timeoutMs > 60000 ? `${Math.round(args.timeoutMs / 1000)}s limit`
            : undefined
        }
        open={expanded}
        onToggle={() => setExpanded((e) => !e)}
        actions={offloadPending && !isRunning && !offload.loading ? (
          <RowAction title={offload.error ? 'Retry loading the output' : 'Show output'} onClick={offload.load}>
            <Download className="w-3 h-3" />
            {offload.error ? 'Retry' : 'Show output'}
          </RowAction>
        ) : undefined}
      >
        <div className="space-y-1.5">
          <DetailPanel label="Command">
            <DetailPre>{args.command}</DetailPre>
          </DetailPanel>

          {showLive && (
            <DetailPanel label="Output">
              <pre
                ref={outputRef}
                className="max-h-[250px] overflow-y-auto whitespace-pre-wrap break-words font-mono text-xs leading-relaxed text-zinc-700"
              >
                {liveText}
              </pre>
            </DetailPanel>
          )}

          {offloadPending && !isRunning && (
            <>
              {offload.error && <div className="text-xs text-red-600">{offload.error}</div>}
              {rawResult?.head && (
                <DetailPanel label={offload.loading ? 'Loading output…' : 'Output stored in the workspace · preview'}>
                  <DetailPre maxH="max-h-[160px]">{rawResult.head}…</DetailPre>
                </DetailPanel>
              )}
            </>
          )}

          {result && !isRunning && !offloadPending && (
            <>
              {result.stdout && (
                <DetailPanel label={`stdout${durationMeta ? ` · ${durationMeta}` : ''}`}>
                  <DetailPre maxH="max-h-[300px]">{result.stdout}</DetailPre>
                </DetailPanel>
              )}
              {result.stderr && (
                <DetailPanel label="stderr" tone="error">
                  <DetailPre tone="error">{result.stderr}</DetailPre>
                </DetailPanel>
              )}
              {result.error && (
                <DetailPanel label="Error" tone="error">
                  <DetailPre tone="error">{result.error}</DetailPre>
                </DetailPanel>
              )}
              {!result.stdout && !result.stderr && !result.error && (
                <div className="text-xs text-zinc-400">No output · exit {result.exitCode ?? 0}</div>
              )}
            </>
          )}
        </div>
      </ToolRow>
    );
  },
});
